import { useNavigate } from "react-router-dom";
import * as translator from "../../../../utils/translator";
import SubmitButton from "../../../../components/SubmitButton/SubmitButton";
import { MainTracker } from "../../../../PixelTrackers/MainTracker";
import { buildMyAccountReorderUrl, trackSwitchToSubscriptionClick } from "../../../Reorder/ReorderTrackingManager";
import { ReorderTrackingContext, WlReorderSubscriptionOffer } from "../../../Reorder/reorderTypes";
import { computeBasicPlanPricing } from "../../../ChoosePlanCarePlus/pricing";
import { showsSwitchToSubscriptionCta, WlReorderVariant } from "../../../../utils/wlReorderSubscriptionExperiment";
import SwitchToSubscriptionButton from "./SwitchToSubscriptionButton";

interface Props {
  orderHashID: string;
  country: string;
  language: string;
  /** The surface's existing click event name. */
  reorderClickEvent: string;
  reorderButtonClassName?: string;
  wlSubscriptionOffer?: WlReorderSubscriptionOffer | null;
  reorderTrackingContext?: ReorderTrackingContext | null;
  /** WL reorder test arm, assigned by the page. */
  wlReorderVariant?: WlReorderVariant | null;
}

/** My Account Reorder button, plus the B "Switch to subscription" CTA. */
const ReorderActions = ({
  orderHashID,
  country,
  language,
  reorderClickEvent,
  reorderButtonClassName = "",
  wlSubscriptionOffer,
  reorderTrackingContext,
  wlReorderVariant = null,
}: Props) => {
  const navigate = useNavigate();
  const showSwitchToSubscription = Boolean(wlSubscriptionOffer) && showsSwitchToSubscriptionCta(wlReorderVariant);

  const subscriptionTotal = wlSubscriptionOffer
    ? computeBasicPlanPricing(wlSubscriptionOffer.duration, wlSubscriptionOffer.productFees.official_price).headerCharged
    : 0;
  const savingsLabel = wlSubscriptionOffer?.savings ? translator.getPrice(wlSubscriptionOffer.savings, `${language.toLowerCase()}-${country.toUpperCase()}`) : null;

  const onReorder = (e: React.MouseEvent) => {
    e?.stopPropagation?.();
    MainTracker.track("click", reorderClickEvent);
    navigate(buildMyAccountReorderUrl(orderHashID, country, language));
  };

  const onSwitchToSubscription = (e: React.MouseEvent) => {
    e?.stopPropagation?.();
    trackSwitchToSubscriptionClick(reorderTrackingContext, subscriptionTotal, wlReorderVariant);
    navigate(buildMyAccountReorderUrl(orderHashID, country, language, { subscriptionIntent: true }));
  };

  const reorderButton = (
    <SubmitButton
      id={"account-order-card-reorder"}
      className={reorderButtonClassName}
      onSubmit={onReorder}
      placeholder={translator.translate(language, "my_account", "MyOrders/reorder")}
    />
  );

  // Outside arm B: unchanged.
  if (!showSwitchToSubscription) return reorderButton;

  return (
    // Width follows the Reorder button; w-0 + min-w-full stops the CTA from widening it.
    <div className="flex w-fit max-w-full flex-col gap-[8px] max-[1200px]:w-full">
      {reorderButton}
      <div className="w-0 min-w-full">
        <SwitchToSubscriptionButton language={language} savingsLabel={savingsLabel} onClick={onSwitchToSubscription} />
      </div>
    </div>
  );
};

export default ReorderActions;
